/**
 * PF Revamp — rehydrate helper.
 *
 * Exposes a stable callback (`loadDraftIntoStore`) that the "Continue Order"
 * drawer calls when the user picks an existing draft. Flow:
 *   1. Flip `draft.isHydrating = true` so the auto-save subscription skips
 *      the replay that would otherwise trigger on each slice being populated.
 *   2. `draftStore.get(draftId)` — direct adapter call (simpler than
 *      imperatively driving `useDraft` with a refetch). `LocalDraftStore.get`
 *      already performs a `pfVersion` check and returns `null` on mismatch.
 *   3. `hydrateFromDraft(dto)` — cascades into every slice, stamps draftId,
 *      clears dirty.
 *   3b. `dropOrphanedStagingRooms()` — W-01: staging rooms are keyed by the
 *      uploaded photo's `gcFilename`, which carries the purchase session it was
 *      uploaded under. `sessionId` is not persisted, so a resumed draft gets a
 *      NEW session and those photos are unresolvable (broken thumbnails, and —
 *      the P0 — the order could still be placed with its sources gone). Dropping
 *      them here, INSIDE the hydration window, is the sanctioned place: it is
 *      not a component mount write (MODULE-CONTRACT §11.1) and it runs before
 *      any module reads `productConfigs`.
 *   4. Flip `draft.isHydrating = false`.
 *
 * Returns a `ResumeResult` discriminated union so the caller decides what
 * toast / cleanup action to fire for each failure reason — rather than this
 * hook knowing about React Query mutations or the snackbar service. The
 * success arm carries `droppedStagingRooms` for the same reason: the count
 * toast is the caller's business (`ContinueOrderDialog`).
 *
 * On success, the caller is responsible for:
 *   - Cross-referencing the now-hydrated cart against the current catalogue
 *     to detect stale items (`cartSlice.clearStaleItems`).
 *   - Verifying the property still exists (deleted-property path).
 *   Both are handled in `ContinueOrderDialog` which has access to the
 *   React Query hooks those steps require.
 */

import { useCallback } from 'react'

import { draftStore } from 'utils/purchaseFlowDrafts'
import { logger } from 'utils/logging'
import type { PfStep } from 'models/purchaseFlowRevamp'

import { usePfStore } from './store'
import { listSessionFiles } from '../fileHub'

export type ResumeFailReason =
  | 'version-mismatch'
  | 'load-failed'

export type ResumeResult =
  | {
    ok: true
    /** W-01 — staging rooms dropped because their photos belong to a foreign
     *  purchase session. `0` on a clean resume. Caller toasts the count. */
    droppedStagingRooms: number
    /** Files the draft expected that the session no longer holds — they expire
     *  server-side. `0` when everything came back (or when there was nothing to
     *  recover). Caller decides whether to say so. */
    droppedUploads: number
    /** The step the draft was saved on, if it recorded one. */
    lastStep?: PfStep
  }
  | { ok: false; reason: ResumeFailReason }

/**
 * Returns a stable callback that loads a draft by id into the store.
 *
 * Resolves to `{ ok: true }` on success or `{ ok: false, reason }` on
 * known failures. Never rejects — the caller does not need a try/catch.
 */

/**
 * Verifies the files a resumed draft expects against what its purchase session
 * still holds, and prunes the rest.
 *
 * Session files expire, so the persisted per-line list is an EXPECTATION. The
 * session's own listing (`GET …/sessions/{id}/files`) is the fact; a file counts
 * as recovered when its server filename is still listed — the same identity the
 * order sends as an `inputImages` ref, so "shown in the drawer" and "usable on the
 * order" cannot diverge.
 *
 * Returns the expected files that are gone. Runs INSIDE the hydration window, so
 * the pruning writes do not mark the draft dirty.
 *
 * A draft with no persisted session (written before uploads were resumable, or
 * saved before a session existed) has nothing to verify: its expectations are
 * dropped, which is exactly the pre-existing behaviour.
 */
async function reconcileResumedUploads(): Promise<Set<string>> {
  const state = usePfStore.getState()
  const expected = state.attachments
  const allExpected = new Set(Object.values(expected).flat().map((file) => file.gcFilename))
  if (allExpected.size === 0) return allExpected

  const sessionId = state.draft.sessionId
  const workspaceId = state.draft.sessionWorkspaceId
  if (!sessionId || !workspaceId) {
    state.clearAllAttachments()
    return allExpected
  }

  let live: ReadonlySet<string>
  try {
    const listing = await listSessionFiles({
      getSessionId: () => sessionId,
      getWorkspaceId: () => workspaceId,
      getEmail: () => null,
    })
    live = new Set(Object.values(listing.files ?? {}).flat().map((file) => file.filename))
  } catch (err) {
    // The session is gone, expired, or unreachable. Keeping the expectations
    // would show files that cannot be ordered with, so drop them and let the
    // client re-upload — the loud, recoverable failure.
    logger.warn('Could not list session files on resume; dropping recovered uploads', {
      context: { component: 'useLoadDraftIntoStore', action: 'reconcileResumedUploads' },
    })
    usePfStore.getState().clearAllAttachments()
    return allExpected
  }

  const dropped = new Set<string>()
  for (const [cartItemId, files] of Object.entries(expected)) {
    const survived = files.filter((file) => live.has(file.gcFilename))
    files.filter((file) => !live.has(file.gcFilename)).forEach((file) => dropped.add(file.gcFilename))
    if (survived.length === files.length) continue
    if (survived.length === 0) usePfStore.getState().clearAttachments(cartItemId)
    else usePfStore.getState().setAttachments(cartItemId, survived)
  }
  return dropped
}

export function useLoadDraftIntoStore(): (draftId: string) => Promise<ResumeResult> {
  // Intentional empty dep array — all state reads inside the callback go through
  // `usePfStore.getState()` which is a stable singleton, not a hook dep. Adding
  // store action refs here would cause the callback identity to change on every
  // render, breaking memoisation in callers (e.g. ContinueOrderDialog's deps).
  return useCallback(async (draftId: string): Promise<ResumeResult> => {
    const { setHydrating, hydrateFromDraft, markError, resetPersistedState } = usePfStore.getState()

    setHydrating(true)
    try {
      const dto = await draftStore.get(draftId)
      if (!dto) {
        // `get` returns null for both "not found" and "pfVersion mismatch".
        // The list filter in `LocalDraftStore.list` normally prevents the user
        // from clicking a stale-version draft, but a race between list render
        // and a version bump could slip through.
        markError(`Draft not found or version mismatch: ${draftId}`)
        return { ok: false, reason: 'version-mismatch' }
      }
      // The draft replaces the order in hand completely, so nothing the draft does not carry survives
      // from it. The reset ends the hydration window, so it is opened again before the draft lands.
      resetPersistedState()
      setHydrating(true)
      hydrateFromDraft(dto)

      const droppedFiles = await reconcileResumedUploads()

      // W-01 — sweep staging rooms whose photos belong to another session. Read
      // the session id AFTER hydration: a draft that carries one restores it, so
      // its own rooms survive and only foreign ones are dropped; a draft without
      // one (pre-session drafts) still has every session-prefixed key swept.
      const afterHydrate = usePfStore.getState()
      const droppedStagingRooms = afterHydrate.dropOrphanedStagingRooms(afterHydrate.draft.sessionId, droppedFiles)

      return { ok: true, droppedStagingRooms, droppedUploads: droppedFiles.size, lastStep: dto.lastStep }
    }
    catch (err) {
      logger.error('Failed to load draft into store', err, {
        context: { component: 'useLoadDraftIntoStore', action: 'load', metadata: { draftId } },
      })
      markError(err instanceof Error ? err.message : 'Failed to load draft')
      return { ok: false, reason: 'load-failed' }
    }
    finally {
      setHydrating(false)
    }
  }, [])
}
